import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';

const AccordionItem = styled.div`
  margin-bottom: 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: none;
  background: ${colors.sidebarBg};
  color: ${colors.textPrimary};
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(1, 132, 255, 0.35);
  }

  &:focus-visible {
    outline: 2px solid ${colors.primaryBlue};
    outline-offset: 2px;
  }
`;

const Chevron = styled.span`
  flex-shrink: 0;
  font-size: 0.85rem;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const AccordionPanel = styled.div`
  padding: ${({ $open }) => ($open ? '1rem 1.1rem 1.15rem' : '0 1.1rem')};
  max-height: ${({ $open }) => ($open ? '8000px' : '0')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  overflow: hidden;
  transition: max-height 0.35s ease, opacity 0.25s ease, padding 0.25s ease;
`;

const Presentation = styled.p`
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.65;
  margin: 0 0 1rem;
`;

const ProcedureTitle = styled.h3`
  color: ${colors.secondaryBlue};
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 0.65rem;
  font-family: 'Montserrat', sans-serif;
`;

const ProcedureList = styled.ol`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.75;
  margin: 0;
  padding-left: 1.35rem;

  li {
    margin-bottom: 0.55rem;
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const FaqList = styled.ul`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.75;
  margin: 0;
  padding-left: 0;
  list-style: none;

  li {
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  li:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  strong {
    display: block;
    margin-bottom: 0.35rem;
    color: ${colors.textPrimary};
  }
`;

/** Section guide repliable : présentation métier + procédure numérotée. */
export default function GuideAccordionSection({
  id,
  title,
  presentation,
  procedure = [],
  procedureTitle,
  faqItems = [],
  faqTitle,
  defaultOpen = false,
  expandLabel,
  collapseLabel
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const hasProcedure = Array.isArray(procedure) && procedure.length > 0;
  const hasFaq = Array.isArray(faqItems) && faqItems.length > 0;
  const toggleLabel = open ? collapseLabel : expandLabel;

  return (
    <AccordionItem id={id}>
      <AccordionHeader
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        title={toggleLabel}
      >
        <span>{title}</span>
        <Chevron $open={open} aria-hidden>
          ▼
        </Chevron>
      </AccordionHeader>
      <AccordionPanel $open={open} id={panelId} role="region" aria-label={title}>
        {presentation ? <Presentation>{presentation}</Presentation> : null}
        {hasProcedure && procedureTitle ? (
          <>
            <ProcedureTitle>{procedureTitle}</ProcedureTitle>
            <ProcedureList>
              {procedure.map((step, index) => (
                <li key={`${id}-step-${index}`}>{step}</li>
              ))}
            </ProcedureList>
          </>
        ) : null}
        {hasFaq && faqTitle ? (
          <>
            <ProcedureTitle>{faqTitle}</ProcedureTitle>
            <FaqList>
              {faqItems.map((item, index) => (
                <li key={`${id}-faq-${index}`}>
                  <strong>{item.q}</strong>
                  <span>{item.a}</span>
                </li>
              ))}
            </FaqList>
          </>
        ) : null}
      </AccordionPanel>
    </AccordionItem>
  );
}
